import React, { useState, useEffect } from 'react';
import { TikTokRecipe } from '../../types/tiktok';
import { RecipeCreatorForm, RecipePrompt } from './RecipeCreatorForm';
import { RecipeImagePreview } from './RecipeImagePreview';
import { TikTokIngredientMatcher } from './TikTokIngredientMatcher';
import { TikTokRecipeEditor } from './TikTokRecipeEditor';
import { MatchedIngredient } from '../../types/ingredients';

type BackendStatus = 'checking' | 'online' | 'offline';
type AppStep = 'form' | 'image-preview' | 'matching' | 'editor';

export const RecipeCreator: React.FC = () => {
  const [recipe, setRecipe] = useState<TikTokRecipe | null>(null);
  const [generatedImageBase64, setGeneratedImageBase64] = useState<string | null>(null);
  const [finalRecipe, setFinalRecipe] = useState<TikTokRecipe | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backendStatus, setBackendStatus] = useState<BackendStatus>('checking');
  const [currentStep, setCurrentStep] = useState<AppStep>('form');

  const backendUrl = import.meta.env.VITE_BACKEND_URL || 'http://localhost:3001';
  const geminiApiKey = import.meta.env.VITE_GEMINI_API_KEY || '';

  useEffect(() => {
    checkBackend();
  }, []);

  const checkBackend = async () => {
    setBackendStatus('checking');
    try {
      const response = await fetch(`${backendUrl}/health`);
      const isOnline = response.ok;
      setBackendStatus(isOnline ? 'online' : 'offline');
    } catch {
      setBackendStatus('offline');
    }
  };

  const handleSubmit = async (prompt: RecipePrompt) => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${backendUrl}/api/generate-recipe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(prompt),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la génération de la recette');
      }

      const result = await response.json();
      setRecipe(result.data.recipe);
      
      // Si une image a été générée, aller à la prévisualisation
      if (result.data.generatedImage) {
        setGeneratedImageBase64(result.data.generatedImage);
        setCurrentStep('image-preview');
      } else {
        // Sinon, aller directement au matching
        setCurrentStep('matching');
      }
    } catch (err) {
      console.error('Erreur:', err);
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const handleImageConfirm = async (finalImageBase64: string) => {
    if (!recipe) return;
    
    setLoading(true);
    setError(null);

    try {
      // Uploader l'image validée vers Firebase Storage
      const response = await fetch(`${backendUrl}/api/upload-generated-image`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageBase64: finalImageBase64,
          recipeName: recipe.title,
        }),
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'upload de l\'image');
      }

      const result = await response.json();
      
      // Mettre à jour la recette avec l'URL de l'image
      const updatedRecipe = {
        ...recipe,
        image: result.data.imageUrl,
      };
      
      setRecipe(updatedRecipe);
      setCurrentStep('matching');
    } catch (err) {
      console.error('Erreur upload image:', err);
      setError('Impossible d\'uploader l\'image. Continuer sans image ?');
      // Continuer sans image
      setCurrentStep('matching');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSkip = () => {
    setCurrentStep('matching');
  };

  const handleConfirmMatches = (matches: MatchedIngredient[]) => {
    if (!recipe) return;

    const updatedRecipe: TikTokRecipe = {
      ...recipe,
      ingredients: matches.map(match => ({
        id: match.extracted.id,
        name: match.extracted.name,
        amount: match.extracted.amount,
        unit: match.extracted.unit,
        standardIngredientId: match.matched?.id,
        category: match.matched?.category,
      })),
    };

    setFinalRecipe(updatedRecipe);
    setCurrentStep('editor');
  };

  const handleSkipMatching = () => {
    setFinalRecipe(recipe);
    setCurrentStep('editor');
  };

  const handleSaveSuccess = () => {
    alert('✅ Recette créée et sauvegardée avec succès !');
    handleReset();
  };

  const handleCancelEditor = () => {
    setCurrentStep('matching');
  };

  const handleReset = () => {
    setRecipe(null);
    setGeneratedImageBase64(null);
    setFinalRecipe(null);
    setError(null);
    setCurrentStep('form');
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">✨ Créateur de Recettes IA</h2>
            <p className="text-gray-600 mt-1">
              Décrivez la recette de vos rêves, l'IA la créera pour vous
            </p>
          </div>
          {backendStatus === 'online' && (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gradient-to-r from-purple-100 to-blue-100 text-purple-800">
              ✨ IA activée
            </span>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>❌ Erreur:</strong> {error}
            </p>
          </div>
        )}

        {currentStep === 'form' && (
          <RecipeCreatorForm 
            onSubmit={handleSubmit} 
            loading={loading}
            backendStatus={backendStatus}
          />
        )}

        {currentStep === 'image-preview' && recipe && generatedImageBase64 && (
          <RecipeImagePreview
            imageBase64={generatedImageBase64}
            recipeName={recipe.title}
            recipeDescription={recipe.description || ''}
            onConfirm={handleImageConfirm}
            onSkip={handleImageSkip}
            backendUrl={backendUrl}
            geminiApiKey={geminiApiKey}
          />
        )}

        {currentStep === 'matching' && recipe && (
          <TikTokIngredientMatcher
            recipe={recipe}
            onConfirm={handleConfirmMatches}
            onSkip={handleSkipMatching}
          />
        )}

        {currentStep === 'editor' && finalRecipe && (
          <TikTokRecipeEditor 
            recipe={finalRecipe} 
            onSuccess={handleSaveSuccess}
            onCancel={handleCancelEditor}
          />
        )}
      </div>
    </div>
  );
};

